import { getSessionItem, setSessionItem } from 'js/storeManager'

/**
 * The advocate's invite code, carried from the referral page to the checkout.
 *
 * The page and the purchase are several routes apart - the referral page
 * navigates to /upgrade, which mounts AccountFlow, SignUpPayment and finally
 * Payment, and none of them sees the referral route's params. So the code is
 * stashed on landing and read back when the purchase is built, exactly as the
 * Awin and Adtraction click ids already are in Payment.tsx.
 *
 * Session rather than local storage: an invite belongs to this visit. A code
 * left in localStorage would attribute a purchase made days later from a link
 * the visitor no longer remembers opening.
 *
 * referral-service reads this back off PaymentNew.transaction_meta and decides
 * whether it earns anybody a reward. Nothing here judges the code beyond its
 * shape - revoked, expired and self-referred are all the service's business.
 */
export const INVITE_CODE_KEY = 'referralInviteCode'

/**
 * Codes are ten characters of Crockford Base32 today, but the guard is
 * deliberately looser than that: it rejects anything that could not be a code
 * at all - a path segment, a URL, an empty string - without encoding a length
 * this app would then have to change in step with the service.
 */
const PLAUSIBLE_CODE = /^[A-Za-z0-9]{4,32}$/

/** Remembers a code for this visit. Ignores anything implausible, and no-ops under SSR. */
export function rememberInviteCode(code?: string | null): void {
  if (!code || !PLAUSIBLE_CODE.test(code)) return

  // Stored verbatim. The service matches the code exactly, so normalising case
  // here would make links work in the app that then fail to resolve.
  setSessionItem(INVITE_CODE_KEY, code)
}

/** The code for this visit, or null. */
export function readInviteCode(): string | null {
  const code = getSessionItem(INVITE_CODE_KEY)
  if (!code || !PLAUSIBLE_CODE.test(code)) return null

  return code
}
